import { useState } from "react";
import { createPortal } from "react-dom";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
} from "firebase/auth";
import { useTranslation } from "react-i18next";
import { auth, googleProvider } from "../firebase";
import { authErrorKey } from "../lib/authErrors";
import { primaryBtn, secondaryBtn, inputStyle, errorStyle } from "../styles";

export type AuthModalMode = "signin" | "register" | "forgot";
type Mode = AuthModalMode;

interface Props {
  onClose: () => void;
  initialMode?: AuthModalMode;
  /**
   * Called right after a sign-in/register call resolves. Does NOT mean
   * App's `user` state is populated yet — onAuthStateChanged fires
   * asynchronously and awaits a /users/sync round-trip first. Callers
   * that need to redirect once auth is truly ready should wait for that
   * state to update rather than navigating from here directly.
   */
  onAuthenticated?: () => void;
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.36-8.16 2.36-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

export default function AuthModal({ onClose, initialMode = "signin", onAuthenticated }: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const resetForm = (nextMode: Mode) => {
    setMode(nextMode);
    setError(null);
    setPassword("");
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithPopup(auth, googleProvider);
      onAuthenticated?.();
      onClose();
    } catch (err: any) {
      setError(t(authErrorKey(err.code)));
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      onAuthenticated?.();
      onClose();
    } catch (err: any) {
      setError(t(authErrorKey(err.code)));
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) {
      setError(t("auth.errors.displayNameRequired"));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: displayName.trim() });
      onAuthenticated?.();
      onClose();
    } catch (err: any) {
      setError(t(authErrorKey(err.code)));
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/users/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setResetSent(true);
    } catch {
      setError(t(authErrorKey("generic")));
    } finally {
      setLoading(false);
    }
  };

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  };

  const cardStyle: React.CSSProperties = {
    background: "var(--color-surface)",
    border: "1px solid color-mix(in srgb, var(--color-accent) 25%, var(--color-border))",
    borderRadius: "16px",
    // Fixed 2rem padding on a 90%-of-320px box left ~224px of usable content at the smallest
    // supported width (CLAUDE.md: >=320px). min() with vw shrinks padding on narrow viewports
    // without needing useIsMobile, matching the shared `modal` style's width convention below.
    padding: "min(2rem, 7vw)",
    width: "min(400px, 92vw)",
    boxShadow: "0 0 0 1px color-mix(in srgb, var(--color-accent) 12%, transparent), 0 24px 64px var(--color-shadow)",
  };

  const dividerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    margin: "1.25rem 0",
    color: "var(--color-text-dim)",
    fontSize: "0.78rem",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "var(--color-text-muted)",
    marginBottom: "0.35rem",
  };

  return createPortal(
    <div style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <h2 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700, color: "var(--color-text)" }}>
            {mode === "signin"   ? t("auth.signIn")
           : mode === "register" ? t("auth.createAccount")
           :                       t("auth.resetPassword")}
          </h2>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-text-dim)", padding: "0.25rem", lineHeight: 1 }}
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M2 2l12 12M14 2L2 14"/>
            </svg>
          </button>
        </div>

        {/* Forgot password */}
        {mode === "forgot" && (
          resetSent ? (
            <div>
              <p style={{ margin: "0 0 1.25rem", fontSize: "0.9rem", color: "var(--color-text-muted)", lineHeight: 1.6 }}>
                {t("auth.resetSent", { email })}
              </p>
              <button style={{ ...secondaryBtn, width: "100%" }} onClick={() => resetForm("signin")}>
                {t("auth.backToSignIn")}
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword}>
              <p style={{ margin: "0 0 1.25rem", fontSize: "0.9rem", color: "var(--color-text-muted)", lineHeight: 1.6 }}>
                {t("auth.resetInstructions")}
              </p>
              <label style={labelStyle}>{t("auth.email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: "1rem" }}
                placeholder="you@example.com"
                required
                autoFocus
              />
              {error && <p style={{ ...errorStyle, marginBottom: "1rem" }}>{error}</p>}
              <button type="submit" disabled={loading} style={{ ...primaryBtn, width: "100%", marginBottom: "0.75rem" }}>
                {loading ? t("auth.sending") : t("auth.sendResetLink")}
              </button>
              <button type="button" style={{ ...secondaryBtn, width: "100%" }} onClick={() => resetForm("signin")}>
                {t("auth.backToSignIn")}
              </button>
            </form>
          )
        )}

        {/* Sign in */}
        {mode === "signin" && (
          <>
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              style={{
                ...secondaryBtn,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.6rem",
              }}
            >
              <GoogleIcon />
              {t("auth.continueWithGoogle")}
            </button>

            <div style={dividerStyle}>
              <div style={{ flex: 1, height: 1, background: "var(--color-border-subtle)" }} />
              <span>{t("auth.or")}</span>
              <div style={{ flex: 1, height: 1, background: "var(--color-border-subtle)" }} />
            </div>

            <form onSubmit={handleEmailSignIn}>
              <label style={labelStyle}>{t("auth.email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: "0.85rem" }}
                placeholder="you@example.com"
                required
                autoFocus
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.35rem" }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>{t("auth.password")}</label>
                <button
                  type="button"
                  onClick={() => resetForm("forgot")}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.78rem", color: "var(--color-accent)", padding: 0 }}
                >
                  {t("auth.forgotPassword")}
                </button>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(null); }}
                style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: "1rem" }}
                placeholder="••••••••"
                required
              />
              {error && <p style={{ ...errorStyle, marginBottom: "1rem" }}>{error}</p>}
              <button type="submit" disabled={loading} style={{ ...primaryBtn, width: "100%", marginBottom: "1rem" }}>
                {loading ? t("auth.signingIn") : t("auth.signIn")}
              </button>
            </form>

            <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted)", textAlign: "center" }}>
              {t("auth.noAccount")}{" "}
              <button
                type="button"
                onClick={() => resetForm("register")}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-accent)", fontWeight: 600, fontSize: "0.85rem", padding: 0 }}
              >
                {t("auth.createAccount")}
              </button>
            </p>
          </>
        )}

        {/* Register */}
        {mode === "register" && (
          <>
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              style={{
                ...secondaryBtn,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.6rem",
              }}
            >
              <GoogleIcon />
              {t("auth.continueWithGoogle")}
            </button>

            <div style={dividerStyle}>
              <div style={{ flex: 1, height: 1, background: "var(--color-border-subtle)" }} />
              <span>{t("auth.or")}</span>
              <div style={{ flex: 1, height: 1, background: "var(--color-border-subtle)" }} />
            </div>

            <form onSubmit={handleRegister}>
              <label style={labelStyle}>{t("auth.displayName")}</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => { setDisplayName(e.target.value); setError(null); }}
                style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: "0.85rem" }}
                placeholder={t("auth.displayNamePlaceholder")}
                required
                autoFocus
              />
              <label style={labelStyle}>{t("auth.email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: "0.85rem" }}
                placeholder="you@example.com"
                required
              />
              <label style={labelStyle}>{t("auth.password")}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(null); }}
                style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: "1rem" }}
                placeholder={t("auth.passwordPlaceholder")}
                required
              />
              {error && <p style={{ ...errorStyle, marginBottom: "1rem" }}>{error}</p>}
              <button type="submit" disabled={loading} style={{ ...primaryBtn, width: "100%", marginBottom: "1rem" }}>
                {loading ? t("auth.creatingAccount") : t("auth.createAccount")}
              </button>
            </form>

            <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted)", textAlign: "center" }}>
              {t("auth.hasAccount")}{" "}
              <button
                type="button"
                onClick={() => resetForm("signin")}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-accent)", fontWeight: 600, fontSize: "0.85rem", padding: 0 }}
              >
                {t("auth.signIn")}
              </button>
            </p>
          </>
        )}

      </div>
    </div>,
    document.body
  );
}
